import { h, ic, toast } from "../dom.ts";
import { prefs, savePrefs, AVATAR_COLORS } from "../prefs.ts";
import { show } from "../main.ts";
import { getMap, spinGlobe } from "../map.ts";
import { setupScreen } from "./setup.ts";
import { startSolo, startDaily, dailyResult } from "./play.ts";
import { hostRoom, joinRoom } from "./lobby.ts";
import { listRooms } from "../mp.ts";
import { DEFAULT_CONFIG } from "../../../shared/game.ts";

export function homeScreen(opts: { code?: string } = {}): HTMLElement {
  const wide = innerWidth > 820;
  getMap().easeTo({ zoom: wide ? 2.0 : 1.1, center: [getMap().getCenter().lng, 20], duration: 900,
    padding: wide ? { left: Math.min(600, innerWidth * 0.45), right: 0, top: 0, bottom: 0 } : { left: 0, right: 0, top: 0, bottom: innerHeight * 0.45 } });

  const nameInput = h("input", { id: "name", class: "field", value: prefs.name, maxlength: 20, placeholder: "Your name", autocomplete: "nickname", autofocus: !prefs.name || undefined });
  nameInput.addEventListener("input", () => { prefs.name = nameInput.value.trim(); savePrefs(prefs); });

  const swatches = h("div", { class: "swatches", role: "group", "aria-label": "Pin color" },
    AVATAR_COLORS.map(c => {
      const b = h("button", { class: "swatch", style: `background:${c}`, "aria-label": `Pin color ${c}`, "aria-pressed": String(c === prefs.color) });
      b.addEventListener("click", () => {
        prefs.color = c; savePrefs(prefs);
        swatches.querySelectorAll("button").forEach(x => x.setAttribute("aria-pressed", String(x === b)));
      });
      return b;
    }));

  const needName = () => {
    if (prefs.name) return true;
    toast("Add your name first");
    nameInput.focus();
    return false;
  };

  const codeInput = h("input", { id: "code", class: "code-input", maxlength: 4, value: opts.code ?? "", "aria-label": "Room code", autocomplete: "off", inputmode: "text" });
  const doJoin = () => {
    const code = codeInput.value.trim().toUpperCase();
    if (!/^[A-Z0-9]{4}$/.test(code)) { toast("Room codes are 4 letters or numbers"); codeInput.focus(); return; }
    if (needName()) joinRoom(code);
  };
  codeInput.addEventListener("keydown", e => { if (e.key === "Enter") doJoin(); });

  const done = dailyResult();
  const daily = h("button", { id: "daily-btn", class: "mode plain", onclick: () => needName() && startDaily() },
    h("span", { class: "icon-btn", style: "background:var(--panel-2);color:var(--gold);flex:none" }, ic("calendar")),
    h("span", { style: "display:flex;flex-direction:column;gap:2px" },
      h("b", { style: "font:700 16px var(--display);color:var(--text)" }, "Daily challenge"),
      done != null ? h("span", { class: "daily-done" }, `Done today · ${done.toLocaleString()} pts`) : h("span", {}, "5 world capitals, same for everyone")));

  const rooms = h("div", { class: "open-games" }, h("h2", {}, h("span", { class: "dot", style: "background:var(--lime)" }), "Open games right now"),
    h("div", { class: "muted", style: "font-size:14px" }, "Looking for open games…"));
  const refreshRooms = async () => {
    const list = await listRooms();
    if (!document.body.contains(rooms)) return;
    rooms.replaceChildren(rooms.firstChild!,
      ...(list.length ? list.slice(0, 4).map(r => h("div", { class: "room-row" },
        h("span", {}, h("b", {}, `${r.hostName}’s room`), h("small", {}, `${r.summary} · ${r.players}/${r.maxPlayers}`)),
        h("button", { class: "btn sm sky", onclick: () => needName() && joinRoom(r.code) }, "Join")))
        : [h("div", { class: "muted", style: "font-size:14px" }, "No open games right now. Start one and share the code.")]));
  };
  refreshRooms();
  const poll = window.setInterval(() => { if (!document.body.contains(rooms)) clearInterval(poll); else refreshRooms(); }, 6000);

  return h("div", { class: "home" },
    h("div", { class: "home-inner" },
      h("div", { class: "brand" },
        h("img", { src: "/assets/images/geopin-logo-drop-zone.png", alt: "" }),
        h("b", {}, "Geo", h("span", {}, "Pin"))),
      h("h1", {}, "Drop the pin.", h("em", {}, "Learn the world.")),
      h("p", { class: "lede" }, "Find capitals, cities and landmarks on a map with no labels. Play solo, race your friends, or take today’s challenge."),
      h("div", { class: "name-row" }, h("label", { for: "name", class: "sr" }, "Your name"), nameInput, swatches),
      h("div", { class: "modes" },
        h("button", { class: "mode lime", onclick: () => { if (!needName()) return; spinGlobe(false); show(setupScreen({ mode: "solo", config: DEFAULT_CONFIG, onDone: cfg => startSolo(cfg) })); } },
          h("b", {}, "Play solo"), h("span", {}, "Pick any map, any pace")),
        h("button", { class: "mode gold", onclick: () => needName() && hostRoom() },
          h("b", {}, "Play with friends"), h("span", {}, "Create a room, share a link")),
        h("div", { class: "join-box" },
          h("label", { for: "code", style: "font-size:13px;color:var(--muted);white-space:nowrap" }, "Code"),
          codeInput, h("button", { class: "btn sm sky", onclick: doJoin }, "Join")),
        daily),
      rooms));
}
